import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout, Send, Loader2, Bot, Sparkles,
  X, Minimize2, Maximize2, Trash2, Download,
  Shield, Target, Users, FileText, Cpu, GitCompare, Search,
  ArrowRight, Mic, ChevronLeft, Plus, History, Zap,
  Bookmark, FolderOpen, Check,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import apiClient from '../../lib/apiClient';
import { ChatComponentRenderer } from '../ChatWidget/components';
import { dispatchComponentAction } from '../ChatWidget/components/dispatchComponentAction';
import { friendlyToolLabel } from '../ChatWidget/components/toolLabels';
import ArtifactLibrary from './ArtifactLibrary';
import ConversationHistory from './ConversationHistory';
import './SalesAICanvas.css';

// Category tiles for the Google-style home
const QUICK_SUGGESTIONS = [
  "What's my day look like?",
  'Brief me on the account I\'m working',
  'What renewals are coming up?',
  'Draft a follow-up from my last meeting',
];

// Widget type display labels
const WIDGET_TYPE_LABELS = {
  dynamic: 'AI Generated',
  results_summary: 'Results',
  spend_sankey: 'Spending Flow',
  spend_table: 'Spending',
  coverage_gauge: 'Coverage',
  gap_table: 'Gaps',
  product_cards: 'Products',
  comparison_matrix: 'Comparison',
  metrics_grid: 'Metrics',
  account_card: 'Account Intel',
  task_list: 'Tasks',
  suggestion_bar: 'Suggestions',
};

function getWidgetTypeLabel(type) {
  if (!type) return 'Widget';
  return WIDGET_TYPE_LABELS[type] || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Check if a widget has meaningful content to display
 * Returns false for widgets that would render as empty
 */
function hasWidgetContent(widget) {
  if (!widget) return false;

  // Check type-specific emptiness
  switch (widget.type) {
    case 'suggestion-bar':
    case 'suggestion_bar':
      // Empty if no suggestions
      return widget.data?.suggestions?.length > 0 || widget.suggestions?.length > 0;

    case 'results_summary':
      // Empty if no data or zero results with no query
      if (!widget.data) return false;
      const total = widget.data.totalMatches ?? widget.data.total ?? widget.data.count ?? 0;
      const query = widget.data.query || widget.data.searchTerm;
      // Show if we have a query (even with 0 results) or if we have results
      return total > 0 || !!query;

    case 'task_list':
    case 'task-list':
      // Empty if no tasks
      const tasks = widget.data?.urgent || widget.data?.high || widget.data?.other || widget.data?.tasks;
      return Array.isArray(tasks) ? tasks.length > 0 : !!tasks;

    case 'empty_state':
      // Empty state is intentionally empty, but should show
      return true;

    case 'dynamic':
      // Dynamic widgets need a schema
      return !!widget.data?.schema;

    default:
      // For other types, check if there's any data
      return !!widget.data || !!widget.title;
  }
}

function SalesAICanvas({ selectedDepartment = null, selectedAccountList = null, accountListMembers = [] }) {
  const navigate = useNavigate();

  // View modes: 'home' (Google-style), 'canvas' (split view)
  const [viewMode, setViewMode] = useState('home');
  const [messages, setMessages] = useState([]);
  const [canvasWidgets, setCanvasWidgets] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [activeTools, setActiveTools] = useState([]); // Tools currently running
  const [error, setError] = useState(null);
  const [tokenUsage, setTokenUsage] = useState({ total: 0 });
  const [modelInfo, setModelInfo] = useState(null); // tier chosen by the smart AI selector for this turn

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  // Conversation session id — groups this chat's turns into one
  // ai_conversations row on the backend (UUID required by the column)
  const sessionIdRef = useRef(crypto.randomUUID());
  const heroInputRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Auto-scroll chat to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, streamingText]);

  // Focus appropriate input based on view
  useEffect(() => {
    if (viewMode === 'home') {
      heroInputRef.current?.focus();
    } else {
      inputRef.current?.focus();
    }
  }, [viewMode]);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Wave 10: first-open daily digest — once per calendar day, the canvas
  // greets you with the briefing instead of waiting to be asked. Guarded by
  // localStorage so navigation/remounts don't refire it.
  const digestFiredRef = useRef(false);
  useEffect(() => {
    const today = new Date().toDateString();
    if (digestFiredRef.current) return;
    if (localStorage.getItem('sai_digest_date') === today) return;
    digestFiredRef.current = true;
    localStorage.setItem('sai_digest_date', today);
    const timer = setTimeout(() => {
      sendMessage('Good morning — give me my daily digest.');
    }, 600);
    return () => clearTimeout(timer);
  }, []);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || loading || streaming) return;

    const streamId = `fe_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    const startTime = Date.now();

    console.log('[SalesAI][STREAM] Starting request', {
      streamId,
      messageLength: text.trim().length,
      historyLength: messages.length,
    });

    // Transition to canvas view on first message
    if (viewMode === 'home') {
      setViewMode('canvas');
    }

    const userMessage = { role: 'user', content: text.trim(), timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setStreaming(false);
    setStreamingText('');
    setActiveTools([]);
    setError(null);

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      const history = [...messages, userMessage].map(m => ({
        role: m.role,
        content: m.content,
      }));

      // Get auth token for the request. getSession() can hang indefinitely
      // after a token-refresh hiccup (known supabase-js failure mode) —
      // race it with a timeout and fall back to the persisted session so
      // a stuck auth call can never silently strand the chat.
      console.log('[SalesAI][STREAM] Getting auth token', { streamId });
      const { supabase } = await import('../../config/supabase');
      let token = null;
      try {
        const raced = await Promise.race([
          supabase.auth.getSession(),
          new Promise((resolve) => setTimeout(() => resolve(null), 4000)),
        ]);
        token = raced?.data?.session?.access_token || null;
        if (!token) {
          const key = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
          if (key) token = JSON.parse(localStorage.getItem(key))?.access_token || null;
        }
      } catch (authErr) {
        console.warn('[SalesAI][STREAM] Auth token fetch failed', { streamId, error: authErr.message });
      }
      console.log('[SalesAI][STREAM] Auth token obtained', { streamId, hasToken: !!token });

      // Use fetch with streaming
      const requestUrl = `${apiClient.baseUrl}/api/chat/stream`;
      console.log('[SalesAI][STREAM] Sending request', {
        streamId,
        url: requestUrl,
        historyCount: history.length,
      });

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          message: text.trim(),
          history: history.slice(-20),
          sessionId: sessionIdRef.current,
          context: {
            ...(selectedDepartment && {
              department: {
                department_id: selectedDepartment.department_id,
                acronym: selectedDepartment.acronym,
                official_name: selectedDepartment.official_name,
              },
            }),
            ...(selectedAccountList && {
              accountList: {
                id: selectedAccountList.id,
                name: selectedAccountList.name,
                member_count: selectedAccountList.member_count,
              },
              accountListMembers: accountListMembers.map(m => ({
                id: m.id,
                department_id: m.department_id,
                acronym: m.acronym,
                official_name: m.official_name || m.salesforce_account_name,
              })),
            }),
          },
        }),
        signal: abortControllerRef.current.signal,
      });

      console.log('[SalesAI][STREAM] Response received', {
        streamId,
        status: response.status,
        statusText: response.statusText,
        contentType: response.headers.get('content-type'),
        ttfb: Date.now() - startTime,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      setLoading(false);
      setStreaming(true);

      // Read SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedText = '';
      let toolsCompleted = [];
      let suggestedActions = [];
      let eventCount = 0;
      let textDeltaCount = 0;
      let widgetsAdded = 0;
      let modelSelected = null; // tier the backend chose for this turn

      console.log('[SalesAI][STREAM] Starting to read stream', { streamId });

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          console.log('[SalesAI][STREAM] Stream read complete', {
            streamId,
            totalEvents: eventCount,
            textDeltas: textDeltaCount,
            textLength: accumulatedText.length,
            toolsCompleted: toolsCompleted.length,
            widgetsAdded,
            totalDuration: Date.now() - startTime,
          });
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        let currentEventType = null;
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEventType = line.slice(7).trim();
            continue;
          }

          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6);
            if (!jsonStr) continue;

            try {
              const data = JSON.parse(jsonStr);
              // Use the event type from the SSE event line, or infer from data
              const eventType = currentEventType || data.type || inferEventType(data);
              currentEventType = null; // Reset for next event
              eventCount++;

              switch (eventType) {
                case 'stream_start':
                  console.log('[SalesAI][STREAM] stream_start received', {
                    streamId,
                    timestamp: data.timestamp,
                  });
                  break;

                case 'model_selected':
                  console.log('[SalesAI][STREAM] model_selected received', {
                    streamId,
                    tier: data.tier,
                    model: data.model,
                    reason: data.reason,
                    downgraded: data.downgraded,
                  });
                  modelSelected = data;
                  setModelInfo(data);
                  break;

                case 'text_delta':
                  textDeltaCount++;
                  accumulatedText += data.text;
                  setStreamingText(accumulatedText);
                  // Only log every 10th text delta to reduce noise
                  if (textDeltaCount % 50 === 0) {
                    console.log('[SalesAI][STREAM] text_delta progress', {
                      streamId,
                      deltaCount: textDeltaCount,
                      totalLength: accumulatedText.length,
                    });
                  }
                  break;

                case 'tool_start':
                  console.log('[SalesAI][STREAM] tool_start received', {
                    streamId,
                    tool: data.tool,
                    toolId: data.tool_id,
                  });
                  setActiveTools(prev => [...prev, {
                    tool: data.tool,
                    tool_id: data.tool_id,
                    status: 'running',
                    startTime: Date.now(),
                  }]);
                  break;

                case 'tool_result':
                  console.log('[SalesAI][STREAM] tool_result received', {
                    streamId,
                    tool: data.tool,
                    toolId: data.tool_id,
                    durationMs: data.duration_ms,
                    uiComponentCount: data.ui_components?.length || 0,
                    componentTypes: data.ui_components?.map(c => c.type) || [],
                    suggestedActionCount: data.suggested_actions?.length || 0,
                  });

                  // Mark tool as complete
                  setActiveTools(prev => prev.map(t =>
                    t.tool_id === data.tool_id
                      ? { ...t, status: 'complete', duration_ms: data.duration_ms }
                      : t
                  ));
                  toolsCompleted.push({
                    tool: data.tool,
                    duration_ms: data.duration_ms,
                  });

                  // Add UI components immediately as they arrive
                  if (data.ui_components && data.ui_components.length > 0) {
                    const newWidgets = data.ui_components.map((component, idx) => ({
                      id: `widget-${Date.now()}-${idx}`,
                      ...component,
                      timestamp: new Date(),
                      minimized: false,
                      query: text.trim(),
                      fromTool: data.tool,
                      streamId,
                    }));
                    widgetsAdded += newWidgets.length;
                    console.log('[SalesAI][STREAM] Adding widgets to canvas', {
                      streamId,
                      tool: data.tool,
                      widgetCount: newWidgets.length,
                      types: newWidgets.map(w => w.type),
                      totalWidgets: widgetsAdded,
                    });
                    // The new turn takes the stage: demote prior turns' widgets
                    // to minimized so the answer to THIS question isn't competing
                    // with every widget that came before it. Cap the accumulated
                    // rail — beyond 20 widgets, the oldest minimized ones drop
                    // (a 4-query session was piling up 69 dead chips).
                    setCanvasWidgets(prev => {
                      const demoted = prev.map(w => (w.streamId === streamId || w.minimized) ? w : { ...w, minimized: true });
                      const next = [...newWidgets, ...demoted];
                      const CAP = 20;
                      if (next.length <= CAP) return next;
                      // Drop the oldest minimized widgets first (newest prepend,
                      // so walk from the end); expanded widgets are never dropped
                      let toDrop = next.length - CAP;
                      const kept = [];
                      for (let i = next.length - 1; i >= 0; i--) {
                        const w = next[i];
                        if (toDrop > 0 && w.minimized) { toDrop--; continue; }
                        kept.unshift(w);
                      }
                      return kept;
                    });
                  } else {
                    console.log('[SalesAI][STREAM] No UI components from tool', {
                      streamId,
                      tool: data.tool,
                    });
                  }

                  // Collect suggested actions
                  if (data.suggested_actions?.length > 0) {
                    suggestedActions.push(...data.suggested_actions);
                  }
                  break;

                case 'stream_end':
                  console.log('[SalesAI][STREAM] stream_end received', {
                    streamId,
                    toolsUsed: data.tools_used?.map(t => t.tool) || [],
                    uiComponentCount: data.ui_components?.length || 0,
                    suggestedActionCount: data.suggested_actions?.length || 0,
                    usage: data.usage,
                    totalDuration: Date.now() - startTime,
                  });

                  // Final message assembly
                  const finalMessage = {
                    role: 'assistant',
                    content: accumulatedText,
                    tools: data.tools_used || toolsCompleted,
                    usage: data.usage,
                    model: data.model || modelSelected,
                    suggested_actions: data.suggested_actions || suggestedActions.slice(0, 5),
                    timestamp: new Date(),
                  };
                  setMessages(prev => [...prev, finalMessage]);
                  setStreamingText('');
                  setActiveTools([]);

                  // Reconcile canvas with the backend's consolidated component set
                  // (e.g. two day-dashboards from separate tools merged into one).
                  // Only replace when consolidation actually changed the count, so
                  // ordinary single-tool turns don't re-mount their widgets.
                  if (data.ui_components && data.ui_components.length !== widgetsAdded) {
                    const mergedWidgets = data.ui_components.map((component, idx) => ({
                      id: `widget-${Date.now()}-merged-${idx}`,
                      ...component,
                      timestamp: new Date(),
                      minimized: false,
                      query: text.trim(),
                      streamId,
                    }));
                    console.log('[SalesAI][STREAM] Reconciling canvas with merged components', {
                      streamId,
                      streamedCount: widgetsAdded,
                      mergedCount: mergedWidgets.length,
                    });
                    setCanvasWidgets(prev => [
                      ...mergedWidgets,
                      ...prev.filter(w => w.streamId !== streamId),
                    ]);
                  }

                  // Update token usage
                  if (data.usage) {
                    setTokenUsage(prev => ({
                      total: prev.total + (data.usage.total_tokens || 0),
                    }));
                  }

                  console.log('[SalesAI][STREAM] Stream processing complete', {
                    streamId,
                    finalTextLength: accumulatedText.length,
                    totalToolsCompleted: toolsCompleted.length,
                    totalWidgetsAdded: widgetsAdded,
                    totalTokens: data.usage?.total_tokens || 0,
                  });
                  break;

                case 'error':
                  console.error('[SalesAI][STREAM] Error event received', {
                    streamId,
                    message: data.message,
                    code: data.code,
                  });
                  setError(data.message);
                  break;

                default:
                  console.log('[SalesAI][STREAM] Unknown event type', {
                    streamId,
                    eventType,
                    dataKeys: Object.keys(data),
                  });
                  // Handle event/data pairs from SSE
                  if (data.text !== undefined) {
                    accumulatedText += data.text;
                    setStreamingText(accumulatedText);
                  }
              }
            } catch (parseErr) {
              console.warn('[SalesAI][STREAM] Parse error', {
                streamId,
                error: parseErr.message,
                jsonStr: jsonStr.substring(0, 100),
              });
            }
          }
        }
      }
    } catch (err) {
      const duration = Date.now() - startTime;
      if (err.name === 'AbortError') {
        console.log('[SalesAI][STREAM] Request aborted', {
          streamId,
          duration,
        });
      } else {
        console.error('[SalesAI][STREAM] Request failed', {
          streamId,
          error: err.message,
          errorName: err.name,
          stack: err.stack?.substring(0, 300),
          duration,
        });
        setError(err.message || 'Failed to send message');
      }
    } finally {
      console.log('[SalesAI][STREAM] Cleanup', { streamId });
      setLoading(false);
      setStreaming(false);
      setModelInfo(null);
      abortControllerRef.current = null;
    }
  }, [messages, loading, streaming, viewMode, selectedDepartment, selectedAccountList, accountListMembers]);

  // Helper to infer event type from data shape
  function inferEventType(data) {
    if (data.text !== undefined) return 'text_delta';
    if (data.tool && data.status === undefined && data.duration_ms === undefined) return 'tool_start';
    if (data.tool && data.duration_ms !== undefined) return 'tool_result';
    if (data.tools_used !== undefined) return 'stream_end';
    if (data.timestamp && !data.tool) return 'stream_start';
    return 'unknown';
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleComponentAction = useCallback((action) => {
    return dispatchComponentAction(action, {
      sendMessage,
      prefillInput: (value) => {
        setInput(value);
        inputRef.current?.focus();
      },
    });
  }, [sendMessage]);

  const removeWidget = (widgetId) => {
    setCanvasWidgets(prev => prev.filter(w => w.id !== widgetId));
  };

  // ── Wave 13: artifact library (durable anchors over the widget stream) ──
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [savedWidgetIds, setSavedWidgetIds] = useState(new Set());

  // ── Conversation History sidebar (persistent, collapsible) ──
  // Mobile starts collapsed: the sidebar is a fixed overlay there, and
  // opening onto it would cover most of a 390px viewport (slice 7b gap).
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );

  // Restore a past conversation from history
  const handleSelectConversation = useCallback(async (conv) => {
    setMessages([]);
    setCanvasWidgets([]);
    setViewMode('canvas');
    setInput('');
    setError(null);

    // Switch to this conversation's session
    sessionIdRef.current = conv.session_id || conv.id;

    // Fetch the full conversation messages
    try {
      const data = await apiClient.get(`/api/ai-conversations/${conv.id}/messages`);
      if (data.messages && data.messages.length > 0) {
        const restored = data.messages.map((m) => ({
          role: m.role,
          content: m.content,
          timestamp: new Date(m.created_at),
          tools: m.tools_used || [],
          model: m.metadata?.model_info,
        }));
        setMessages(restored);

        // Extract widgets from ui_schema in assistant messages
        const extractedWidgets = [];
        data.messages.forEach((m, msgIdx) => {
          if (m.role === 'assistant' && m.ui_schema) {
            // ui_schema could be a single widget or an array
            const schemas = Array.isArray(m.ui_schema) ? m.ui_schema : [m.ui_schema];
            schemas.forEach((schema, idx) => {
              if (schema && schema.type) {
                extractedWidgets.push({
                  id: `widget-restored-${Date.now()}-${msgIdx}-${idx}`,
                  ...schema,
                  timestamp: new Date(m.created_at),
                  minimized: true, // Start minimized for past conversations
                });
              }
            });
          }
        });

        if (extractedWidgets.length > 0) {
          // Show only the most recent widgets expanded
          const widgetsToShow = extractedWidgets.slice(-3).map(w => ({ ...w, minimized: false }));
          const olderWidgets = extractedWidgets.slice(0, -3);
          setCanvasWidgets([...widgetsToShow, ...olderWidgets]);
        }
      }
    } catch (err) {
      console.error('[SalesAI] Failed to restore conversation:', err.message);
      setError('Failed to restore conversation');
    }
  }, []);

  const saveWidget = async (widget) => {
    // Strip canvas-ephemeral fields; what's left IS the artifact schema
    const { id, minimized, timestamp, streamId, query, fromTool, ...uiSchema } = widget;
    try {
      await apiClient.post('/api/artifacts', {
        name: widget.title || getWidgetTypeLabel(widget.type),
        widget_type: widget.type,
        department_id: selectedDepartment?.department_id || null,
        account_label: selectedDepartment?.acronym || selectedDepartment?.official_name || null,
        ui_schema: uiSchema,
        provenance: {
          saved_from: 'canvas',
          query: query || null,
          tool: fromTool || null,
          stream_id: streamId || null,
          session_id: sessionIdRef.current,
        },
      });
      setSavedWidgetIds((prev) => new Set(prev).add(widget.id));
    } catch (e) {
      console.error('[SalesAI] Failed to save artifact:', e.message);
    }
  };

  const restoreWidget = (uiSchema) => {
    const restored = {
      id: `widget-${Date.now()}-restored`,
      ...uiSchema,
      timestamp: new Date(),
      minimized: false,
      streamId: null,
    };
    setCanvasWidgets((prev) => [restored, ...prev]);
    setLibraryOpen(false);
    if (viewMode !== 'canvas') setViewMode('canvas');
  };

  const saveFromHistory = async (historyItem) => {
    const w = historyItem.widget || {};
    try {
      await apiClient.post('/api/artifacts', {
        name: w.title || getWidgetTypeLabel(w.type),
        widget_type: w.type,
        department_id: selectedDepartment?.department_id || null,
        account_label: selectedDepartment?.acronym || selectedDepartment?.official_name || null,
        ui_schema: w,
        provenance: {
          saved_from: 'history',
          message_id: historyItem.message_id,
          conversation_id: historyItem.conversation_id,
          generated_at: historyItem.generated_at,
        },
      });
    } catch (e) {
      console.error('[SalesAI] Failed to save from history:', e.message);
    }
  };

  const toggleWidgetMinimize = (widgetId) => {
    setCanvasWidgets(prev => prev.map(w =>
      w.id === widgetId ? { ...w, minimized: !w.minimized } : w
    ));
  };

  const clearCanvas = () => {
    setCanvasWidgets([]);
  };

  const exportCanvas = () => {
    const exportData = {
      timestamp: new Date().toISOString(),
      widgets: canvasWidgets,
      conversation: messages,
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales-ai-session-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const startNewSession = () => {
    setMessages([]);
    setCanvasWidgets([]);
    setViewMode('home');
    setInput('');
    setError(null);
    // Fresh conversation on the backend too
    sessionIdRef.current = crypto.randomUUID();
  };

  // Render the Google-style home view
  const greeting = () => {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  };

  const renderHomeView = () => (
    <div className="sac-home sac-home-minimal">
      <div className="sac-home-header">
        <div className="sac-home-header-left">
          {/* Mobile-only: the sidebar collapses to width 0 there, taking
              its own expand button with it — this is the way back in. */}
          <button
            type="button"
            className="sac-history-toggle"
            onClick={() => setSidebarCollapsed(prev => !prev)}
            aria-label={sidebarCollapsed ? 'Show conversation history' : 'Hide conversation history'}
          >
            <History size={18} />
          </button>
        </div>
        <div className="sac-home-header-right" />
      </div>

      <div className="sac-hero sac-hero-minimal">
        <h1 className="sac-greeting">{greeting()}</h1>

        <div className="sac-hero-search">
          <div className="sac-hero-search-inner">
            <input
              ref={heroInputRef}
              type="text"
              className="sac-hero-input"
              placeholder="Ask anything…"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button className="sac-hero-mic" title="Voice input (coming soon)">
              <Mic size={18} />
            </button>
            {input.trim() && (
              <button
                className="sac-hero-submit"
                onClick={() => sendMessage(input)}
                disabled={loading}
              >
                {loading ? <Loader2 size={18} className="sac-spin" /> : <ArrowRight size={18} />}
              </button>
            )}
          </div>

          <div className="sac-suggestions-minimal">
            {QUICK_SUGGESTIONS.map((suggestion, i) => (
              <button
                key={i}
                className="sac-suggestion-line"
                onClick={() => sendMessage(suggestion)}
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Render the canvas view (split layout)
  const renderCanvasView = () => (
    <div className="sac-canvas-layout">
      {/* Header */}
      <header className="sac-header">
        <div className="sac-header-left">
          <button
            type="button"
            className="sac-history-toggle"
            onClick={() => setSidebarCollapsed(prev => !prev)}
            aria-label={sidebarCollapsed ? 'Show conversation history' : 'Hide conversation history'}
          >
            <History size={18} />
          </button>
          <button
            className="sac-back-btn"
            onClick={startNewSession}
            title="Back to home"
          >
            <ChevronLeft size={20} />
          </button>
          <div className="sac-logo" onClick={startNewSession}>
            <Sparkles size={20} />
            <span>Sales AI</span>
          </div>
        </div>
        <div className="sac-header-center">
          <div className="sac-session-info">
            <span className="sac-session-count">
              {messages.filter(m => m.role === 'user').length} queries
            </span>
            {canvasWidgets.filter(hasWidgetContent).length > 0 && (
              <span className="sac-widget-count">
                {canvasWidgets.filter(hasWidgetContent).length} widgets
              </span>
            )}
          </div>
        </div>
        <div className="sac-header-right">
          {tokenUsage.total > 0 && (
            <span className="sac-token-badge">
              {(tokenUsage.total / 1000).toFixed(1)}k tokens
            </span>
          )}
          <button className="sac-header-btn" onClick={exportCanvas} title="Export session">
            <Download size={18} />
          </button>
          <button className="sac-header-btn" onClick={startNewSession} title="New session">
            <Plus size={18} />
          </button>
        </div>
      </header>

      <div className="sac-main">
        {/* Chat Panel */}
        <div className="sac-chat-panel">
          {/* Messages */}
          <div className="sac-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`sac-message sac-message-${msg.role}`}>
                <div className="sac-message-header">
                  {msg.role === 'user' ? (
                    <span className="sac-message-author">You</span>
                  ) : (
                    <>
                      <Bot size={14} />
                      <span className="sac-message-author">Sales AI</span>
                    </>
                  )}
                  {msg.model && (
                    <span
                      className={`sac-model-chip sac-model-${msg.model.tier}`}
                      title={`${msg.model.model}${msg.model.category ? ` · ${msg.model.category.replace(/_/g, ' ')}` : ''} — ${msg.model.reason}${msg.model.downgraded ? ' (budget downgrade)' : ''}`}
                    >
                      <Zap size={10} />
                      {msg.model.label}
                    </span>
                  )}
                  {msg.timestamp && (
                    <span className="sac-message-time">
                      {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
                <div className="sac-message-content">
                  {msg.role === 'assistant' ? (
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  ) : (
                    msg.content
                  )}
                </div>
                {msg.tools && msg.tools.length > 0 && (
                  <div className="sac-tools">
                    {msg.tools.map((t, j) => (
                      <span key={j} className="sac-tool-tag">
                        {friendlyToolLabel(t.tool)} ({t.duration_ms}ms)
                      </span>
                    ))}
                  </div>
                )}
                {msg.suggested_actions && msg.suggested_actions.length > 0 && (
                  <div className="sac-inline-actions">
                    {msg.suggested_actions.slice(0, 3).map((action, idx) => (
                      <button
                        key={idx}
                        className="sac-inline-action"
                        onClick={() => handleComponentAction({ type: 'query', query: action.query })}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Loading state - before streaming starts */}
            {loading && !streaming && (
              <div className="sac-typing">
                <Bot size={14} />
                <div className="sac-typing-dots">
                  <span /><span /><span />
                </div>
              </div>
            )}

            {/* Streaming state - show live text and active tools */}
            {streaming && (
              <div className="sac-message sac-message-assistant sac-message-streaming">
                <div className="sac-message-header">
                  <Bot size={14} />
                  <span className="sac-message-author">Sales AI</span>
                  <span className="sac-streaming-indicator">
                    <Loader2 size={12} className="sac-spin" />
                    streaming
                  </span>
                  {modelInfo && (
                    <span
                      className={`sac-model-chip sac-model-${modelInfo.tier}`}
                      title={`${modelInfo.model}${modelInfo.category ? ` · ${modelInfo.category.replace(/_/g, ' ')}` : ''} — ${modelInfo.reason}${modelInfo.downgraded ? ' (budget downgrade)' : ''}`}
                    >
                      <Zap size={10} />
                      {modelInfo.label}
                    </span>
                  )}
                </div>

                {/* Active tools indicator */}
                {activeTools.length > 0 && (
                  <div className="sac-active-tools">
                    {activeTools.map((tool, idx) => (
                      <div key={idx} className={`sac-active-tool sac-tool-${tool.status}`}>
                        {tool.status === 'running' ? (
                          <Loader2 size={12} className="sac-spin" />
                        ) : (
                          <span className="sac-tool-check">✓</span>
                        )}
                        <span className="sac-tool-name">
                          {friendlyToolLabel(tool.tool)}…
                        </span>
                        {tool.status === 'complete' && (
                          <span className="sac-tool-duration">{tool.duration_ms}ms</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Streaming text */}
                {streamingText && (
                  <div className="sac-message-content">
                    <ReactMarkdown>{streamingText}</ReactMarkdown>
                    <span className="sac-cursor">▊</span>
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="sac-error">{error}</div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Working account context chip */}
          {selectedAccountList ? (
            <div className="sac-context-chip sac-context-chip-list" title={`${selectedAccountList.name} (${accountListMembers.length} accounts)`}>
              <Users size={12} />
              {selectedAccountList.name}
              <span className="sac-context-count">{accountListMembers.length}</span>
            </div>
          ) : selectedDepartment && (
            <div className="sac-context-chip" title={selectedDepartment.official_name}>
              <Target size={12} />
              Working: {selectedDepartment.acronym || selectedDepartment.official_name}
            </div>
          )}

          {/* Input */}
          <div className="sac-input-area">
            <textarea
              ref={inputRef}
              className="sac-input"
              placeholder="Ask a follow-up question..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={loading || streaming}
            />
            <button
              className="sac-send-btn"
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || loading || streaming}
            >
              {(loading || streaming) ? <Loader2 size={18} className="sac-spin" /> : <Send size={18} />}
            </button>
          </div>
        </div>

        {/* Canvas Panel */}
        <div className="sac-canvas-panel">
          <div className="sac-canvas-header">
            <span className="sac-canvas-title">
              <Layout size={16} />
              Canvas
            </span>
            <div className="sac-canvas-controls">
              <button
                className="sac-canvas-btn"
                onClick={() => setLibraryOpen(true)}
                title="Library — saved views & history"
              >
                <FolderOpen size={14} />
              </button>
              <button
                className="sac-canvas-btn"
                onClick={clearCanvas}
                title="Clear canvas"
                disabled={canvasWidgets.length === 0}
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>

          <div className="sac-canvas-content">
            {(() => {
              // Filter out widgets with no content
              const visibleWidgets = canvasWidgets.filter(hasWidgetContent);

              if (visibleWidgets.length === 0) {
                return (
                  <div className="sac-canvas-empty">
                    <Layout size={48} strokeWidth={1} />
                    <p>Your canvas is empty</p>
                    <span>Responses will generate interactive widgets here</span>
                  </div>
                );
              }

              return (
                <div className="sac-widget-grid">
                  {visibleWidgets.map((widget, idx) => (
                  <div
                    key={widget.id}
                    className={`sac-widget ${widget.minimized ? 'sac-widget-minimized' : ''} ${idx === 0 && !widget.minimized ? 'sac-widget-hero' : ''}`}
                  >
                    <div className="sac-widget-header">
                      <span className="sac-widget-type-badge">{getWidgetTypeLabel(widget.type)}</span>
                      {widget.title && (
                        <span className="sac-widget-title">{widget.title}</span>
                      )}
                      <div className="sac-widget-controls">
                        <button
                          className={savedWidgetIds.has(widget.id) ? 'sac-widget-saved' : ''}
                          onClick={() => saveWidget(widget)}
                          title={savedWidgetIds.has(widget.id) ? 'Saved to library' : 'Save to library'}
                        >
                          {savedWidgetIds.has(widget.id) ? <Check size={12} /> : <Bookmark size={12} />}
                        </button>
                        <button
                          onClick={() => toggleWidgetMinimize(widget.id)}
                          title={widget.minimized ? 'Expand' : 'Minimize'}
                        >
                          {widget.minimized ? <Maximize2 size={12} /> : <Minimize2 size={12} />}
                        </button>
                        <button onClick={() => removeWidget(widget.id)} title="Remove">
                          <X size={12} />
                        </button>
                      </div>
                    </div>
                    {!widget.minimized && (
                      <div className="sac-widget-body">
                        <ChatComponentRenderer
                          components={[widget]}
                          onAction={handleComponentAction}
                        />
                      </div>
                    )}
                  </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`sac-container sac-view-${viewMode} ${sidebarCollapsed ? 'sac-sidebar-collapsed' : ''}`}>
      {/* Persistent conversation history sidebar */}
      <ConversationHistory
        inline
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(prev => !prev)}
        onSelectConversation={handleSelectConversation}
        onNewConversation={startNewSession}
        onBack={() => navigate('/home')}
        currentSessionId={sessionIdRef.current}
      />
      {/* Mobile: the expanded sidebar is a fixed overlay — a backdrop
          gives it an outside-tap close (slice 7b gap) */}
      {!sidebarCollapsed && (
        <div
          className="sac-sidebar-backdrop"
          onClick={() => setSidebarCollapsed(true)}
          aria-hidden="true"
        />
      )}

      {/* Main content area */}
      <div className="sac-main-area">
        {viewMode === 'home' ? renderHomeView() : renderCanvasView()}
      </div>

      {/* Artifact library overlay */}
      <ArtifactLibrary
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onRestore={restoreWidget}
        onSaveFromHistory={saveFromHistory}
      />
    </div>
  );
}

export default SalesAICanvas;
